import { Check, ChevronsUpDown, Building2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCompany } from '@/contexts/CompanyContext';
import { useState } from 'react';

export function CompanySelector() {
  const { selectedCompany, companies, setSelectedCompany, isLoading } = useCompany();
  const [open, setOpen] = useState(false);

  // Handle edge case: no companies
  if (!isLoading && companies.length === 0) {
    return (
      <div className="px-6 py-3 border-b">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span>No companies available</span>
        </div>
      </div>
    );
  }

  // Handle edge case: single company (show but disable interaction)
  if (!isLoading && companies.length === 1) {
    return (
      <div className="px-6 py-3 border-b">
        <div className="flex items-center gap-2 text-sm">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{companies[0].name}</span>
          {companies[0].code && (
            <span className="text-xs text-muted-foreground">({companies[0].code})</span>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="px-6 py-3 border-b">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading companies...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-3 border-b">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select a company"
            className="w-full justify-between h-auto py-2 px-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex flex-col items-start min-w-0">
                <span className="font-medium text-sm truncate w-full text-left">
                  {selectedCompany?.name || 'Select company'}
                </span>
                {(selectedCompany?.code || selectedCompany?.currency) && (
                  <span className="text-xs text-muted-foreground">
                    {[selectedCompany.code, selectedCompany.currency].filter(Boolean).join(' • ')}
                  </span>
                )}
              </div>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search companies..." />
            <CommandList>
              <CommandEmpty>No company found.</CommandEmpty>
              <CommandGroup>
                {companies.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={company.name}
                    onSelect={() => {
                      setSelectedCompany(company);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Check
                        className={cn(
                          'h-4 w-4 flex-shrink-0',
                          selectedCompany?.id === company.id
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-medium text-sm truncate">
                          {company.name}
                        </span>
                        {(company.code || company.currency) && (
                          <span className="text-xs text-muted-foreground">
                            {[company.code, company.currency].filter(Boolean).join(' • ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
